import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NgxMaskModule } from 'ngx-mask';
import { firstValueFrom } from 'rxjs';

import { LocalisePipe } from '../../../../modules/pipes/localise.pipe';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { FormControlNameExport } from '../../../directives/form-control-name-export.directive';
import { ProfileService } from '../../../profile/services/profile.service';
import { DisableAutofillDirective } from '../../../shared/directives/disable-autofill.directive';
import { SharedService } from '../../../shared/services/shared.service';
import { InviteSignupProcessService } from '../../services/invite-signup-process.service';

@Component({
  selector: 'app-invite-signup-process-phone',
  templateUrl: './invite-signup-process-phone.component.html',
  styleUrls: ['./invite-signup-process-phone.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatError,
    MatIconModule,
    NgxMaskModule,
    DisableAutofillDirective,
    LocalisePipe,
    FormControlNameExport,
    MatIconButton,
    MatButton,
  ],
})
export class InviteSignupProcessPhoneComponent implements OnInit {
  @Input() prohibitClosing = false;

  @ViewChild('inputPhone', { static: false })
  inputPhone: ElementRef<HTMLInputElement>;

  public error = '';
  public loading = false;
  public readonly alreadyRegisteredText =
    'User with such phone number is already registered';
  public readonly waitBeforeRequestingAnotherCodeText =
    'Please wait 30 seconds before requesting another code';
  public alreadyRegistered = false;
  public waitBeforeRequestingAnotherCode = 0;
  private waitBeforeRequestingAnotherCodeTimer?: number | any;

  constructor(
    public inviteSignupProcessService: InviteSignupProcessService,
    private sharedService: SharedService,
    private authenticationService: AuthenticationService,
    private profileService: ProfileService,
    private dialogRef: MatDialogRef<InviteSignupProcessPhoneComponent>,
  ) {}

  ngOnInit(): void {
    this.dialogRef.disableClose = this.prohibitClosing;

    if (this.authenticationService.getProfileState().isPhoneNumberConfirmed) {
      this.inviteSignupProcessService.nextStep();
      return;
    }

    this.prefillPhone();
  }

  public async prefillPhone(): Promise<void> {
    this.loading = true;

    try {
      const phone = await firstValueFrom(this.profileService.prefillPhone());
      if (phone.phoneNumber) {
        this.inviteSignupProcessService.phoneForm.controls.phoneNumber.setValue(
          phone.phoneNumber.slice(1),
        );
        this.inviteSignupProcessService.phoneForm.controls.phoneNumber.disable();
        // hack for ngx-mask
        setTimeout(() =>
          this.inputPhone.nativeElement.dispatchEvent(new Event('input')),
        );
      } else {
        this.inviteSignupProcessService.phoneForm.controls.phoneNumber.enable();
      }
    } catch (e) {
      this.error = this.sharedService.getErrorText(e, true);
    }

    this.loading = false;
  }

  public async next(): Promise<void> {
    if (this.inviteSignupProcessService.phoneForm.invalid) {
      return;
    }

    this.loading = true;

    try {
      await this.inviteSignupProcessService.initiatePreVerifyPhone();
      this.inviteSignupProcessService.nextStep();
    } catch (e) {
      this.error = this.sharedService.getErrorText(
        e,
        true,
        this.inviteSignupProcessService.phoneForm,
      );
      this.alreadyRegistered = this.error === this.alreadyRegisteredText;

      if (this.error === this.waitBeforeRequestingAnotherCodeText) {
        if (this.waitBeforeRequestingAnotherCodeTimer) {
          clearInterval(this.waitBeforeRequestingAnotherCodeTimer);
        }

        this.waitBeforeRequestingAnotherCode = 30;

        this.waitBeforeRequestingAnotherCodeTimer = setInterval(() => {
          this.waitBeforeRequestingAnotherCode--;
          if (this.waitBeforeRequestingAnotherCode === 0) {
            clearInterval(this.waitBeforeRequestingAnotherCodeTimer);
            this.error = '';
          }
        }, 1_000);
      }
    }

    this.loading = false;
  }
}
